import { useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldCheck, Database, CreditCard, Trash2, Mail, Cookie } from "lucide-react";

const Block = ({ icon: Icon, title, children }) => (
  <section className="rounded-3xl border border-[#e5e0d8] bg-white p-5">
    <h2 className="mb-2 flex items-center gap-2 font-serif text-2xl font-bold text-[#2d2824]">
      <Icon className="h-5 w-5 text-[#8a2be2]" /> {title}
    </h2>
    <div className="space-y-2 text-sm leading-relaxed text-[#6e655b]">{children}</div>
  </section>
);

export default function PrivacyPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen px-5 py-8" data-testid="privacy-page">
      <div className="mx-auto max-w-2xl space-y-4">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))}
          className="flex items-center gap-1.5 rounded-full border border-[#e5e0d8] bg-white px-3 py-2 text-sm font-bold text-[#2d2824] hover:bg-[#f3efea]"
          data-testid="privacy-back"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </button>

        <header>
          <h1 className="font-serif text-4xl font-bold text-[#2d2824] sm:text-5xl">
            Privacy & your data
          </h1>
          <p className="mt-1 text-sm font-semibold text-[#a89e92]">
            Foinn.org · Folk Tune Finder — GDPR summary, in plain English
          </p>
        </header>

        <Block icon={Database} title="What Foinn.org stores">
          <p>
            <b>If you browse as a guest:</b> nothing leaves your device except the tune searches you
            run. Your daily search count and your favourites are kept in your browser’s local
            storage only.
          </p>
          <p>
            <b>If you sign in with Google:</b> we store your name, email address and profile picture
            URL (provided by Google), your favourite tunes, your plan and remaining AI analyses, the
            number of searches you made per day, and a record of your purchases. Nothing else — no
            tracking profiles, no advertising data, no data sold or shared with third parties.
          </p>
          <p>
            Tune data comes from the public thesession.org database. Expert analyses generated by AI
            are stored per tune (not per person) so they can be reused for free by everyone.
          </p>
        </Block>

        <Block icon={ShieldCheck} title="Legal basis & processors">
          <p>
            We process your data to provide the service you asked for (contract) and, for payments,
            to meet accounting obligations. Processors: <b>Google</b> (sign-in),
            <b> Stripe</b> (payments — we never see or store your card details),
            <b> Google Gemini</b> (AI analysis of public tune metadata only, never your personal
            data), and our hosting provider for the database.
          </p>
        </Block>

        <Block icon={CreditCard} title="Cancelling a subscription">
          <p>
            Foinn.org Premium (€2/month or €20/year) renews automatically. You can stop it at any time,
            in two clicks and without contacting anyone:
          </p>
          <ol className="ml-4 list-decimal space-y-1">
            <li>Open <b>Your account</b> (the avatar button at the top of the page).</li>
            <li>
              Click <b>“Cancel renewal”</b>. Billing stops immediately and you keep Premium until
              the end of the period you already paid for.
            </li>
          </ol>
          <p>
            <b>Manage subscription &amp; invoices</b> in the same panel opens Stripe’s secure portal,
            where you can also download invoices or change your payment method. Donations and AI
            packs are one-off payments — there is nothing to cancel.
          </p>
        </Block>

        <Block icon={Trash2} title="Your rights: copy or erase everything">
          <p>
            Open <b>Your account</b> → <b>Your data</b>:
          </p>
          <ul className="ml-4 list-disc space-y-1">
            <li>
              <b>Download my data</b> — a full JSON copy of everything linked to your account
              (portability, art. 20 GDPR).
            </li>
            <li>
              <b>Delete my account</b> — erasure (art. 17 GDPR). This is <b>permanent and
              irreversible</b>: your account, favourites, search counters and remaining analyses are
              deleted, and any active subscription is cancelled straight away. Payment records are
              kept without your identity, because tax law requires it.
            </li>
          </ul>
          <p>
            You may also ask us to correct your data or object to processing. Signing out alone does
            not delete anything.
          </p>
        </Block>

        <Block icon={Cookie} title="Cookies">
          <p>
            Foinn.org sets a single essential cookie (<code>session_token</code>) to keep you signed in,
            plus local storage for your guest favourites and search counter. No analytics cookies, no
            advertising cookies, so there is nothing to consent to.
          </p>
        </Block>

        <Block icon={Mail} title="Contact">
          <p>
            Questions about your data, or want it removed manually? Write to{" "}
            <a
              href="mailto:cedric.curtelin@outlook.com"
              className="font-bold underline"
              data-testid="privacy-contact-email"
            >
              cedric.curtelin@outlook.com
            </a>
            . We answer requests within 30 days, as required by GDPR.
          </p>
        </Block>

        <p className="pb-8 text-center text-xs text-[#a89e92]">
          Foinn.org keeps only what it needs to show you tunes. Last updated: September 2026.
        </p>
      </div>
    </div>
  );
}
